/**
 * 加密模块
 * 移植自 xhshow-go/xhshow/crypto.go
 */
import {
  HEX_KEY,
  VERSION_BYTES,
  ENV_FINGERPRINT_XOR_KEY,
  ENV_FINGERPRINT_TIME_OFFSET_MIN,
  ENV_FINGERPRINT_TIME_OFFSET_MAX,
  SEQUENCE_VALUE_MIN,
  SEQUENCE_VALUE_MAX,
  WINDOW_PROPS_LENGTH_MIN,
  WINDOW_PROPS_LENGTH_MAX,
  CHECKSUM_VERSION,
  CHECKSUM_XOR_KEY,
  CHECKSUM_FIXED_TAIL,
} from '../config.js';
import { generateRandomInt, generateRandomByteInRange, intToLeBytes } from '../utils/index.js';

/**
 * XOR 变换数组
 */
export function xorTransformArray(sourceIntegers: number[]): Uint8Array {
  const resultBytes = new Uint8Array(sourceIntegers.length);
  const keyBytes = Buffer.from(HEX_KEY, 'hex');
  const keyLength = keyBytes.length;

  for (let index = 0; index < sourceIntegers.length; index++) {
    if (index < keyLength) {
      resultBytes[index] = (sourceIntegers[index] ^ keyBytes[index]) & 0xff;
    } else {
      resultBytes[index] = sourceIntegers[index] & 0xff;
    }
  }

  return resultBytes;
}

/**
 * 环境指纹 A (带校验和)
 */
export function envFingerprintA(ts: number, xorKey: number): number[] {
  const buf = Buffer.allocUnsafe(8);
  buf.writeBigUInt64LE(BigInt(ts));

  let sum1 = 0;
  for (let i = 1; i < 5; i++) {
    sum1 += buf[i];
  }

  let sum2 = 0;
  for (let i = 5; i < 8; i++) {
    sum2 += buf[i];
  }

  const mark = ((sum1 & 0xff) + sum2) & 0xff;
  buf[0] = mark;

  const res: number[] = [];
  for (let i = 0; i < 8; i++) {
    res.push(buf[i] ^ xorKey);
  }

  return res;
}

/**
 * 环境指纹 B (无加密)
 */
export function envFingerprintB(ts: number): number[] {
  const buf = Buffer.allocUnsafe(8);
  buf.writeBigUInt64LE(BigInt(ts));

  const res: number[] = [];
  for (let i = 0; i < 8; i++) {
    res.push(buf[i]);
  }

  return res;
}

/**
 * 构建 Payload 数组
 */
export function buildPayloadArray(
  hexParameter: string,
  a1Value: string,
  appIdentifier: string,
  stringParam: string,
  timestamp: number
): number[] {
  const payload: number[] = [];

  // 1. 版本字节
  payload.push(...VERSION_BYTES);

  // 2. 随机种子
  const seed = generateRandomInt();
  const seedBytes = intToLeBytes(seed, 4);
  payload.push(...seedBytes);
  const seedByte0 = seedBytes[0];

  // 3. 时间戳处理
  const ts = timestamp || Date.now();
  const tsMillis = Math.floor(ts);

  // 环境指纹 A
  const fpA = envFingerprintA(tsMillis, ENV_FINGERPRINT_XOR_KEY);
  payload.push(...fpA);

  // 4. 环境指纹 B (时间偏移)
  const timeOffset = generateRandomByteInRange(
    ENV_FINGERPRINT_TIME_OFFSET_MIN,
    ENV_FINGERPRINT_TIME_OFFSET_MAX
  );
  const tsOffsetMillis = Math.floor(ts - timeOffset);
  const fpB = envFingerprintB(tsOffsetMillis);
  payload.push(...fpB);

  // 5. 序列值
  const seqVal = generateRandomByteInRange(SEQUENCE_VALUE_MIN, SEQUENCE_VALUE_MAX);
  const seqBytes = intToLeBytes(seqVal, 4);
  payload.push(...seqBytes);

  // 6. 窗口属性长度
  const winLen = generateRandomByteInRange(WINDOW_PROPS_LENGTH_MIN, WINDOW_PROPS_LENGTH_MAX);
  const winBytes = intToLeBytes(winLen, 4);
  payload.push(...winBytes);

  // 7. URI 长度
  const uriLen = stringParam.length;
  const uriLenBytes = intToLeBytes(uriLen, 4);
  payload.push(...uriLenBytes);

  // 8. MD5 XOR 段
  const md5Bytes = Buffer.from(hexParameter, 'hex');
  for (let i = 0; i < 8; i++) {
    payload.push(md5Bytes[i] ^ seedByte0);
  }

  // 9. A1 长度
  payload.push(52);

  // 10. A1 内容
  const a1Bytes = Buffer.from(a1Value, 'utf8');
  const a1Padded = Buffer.alloc(52);
  a1Bytes.copy(a1Padded, 0, 0, Math.min(a1Bytes.length, 52));
  for (let i = 0; i < 52; i++) {
    payload.push(a1Padded[i]);
  }

  // 11. 来源长度
  payload.push(10);

  // 12. 来源内容
  const srcBytes = Buffer.from(appIdentifier, 'utf8');
  const srcPadded = Buffer.alloc(10);
  srcBytes.copy(srcPadded, 0, 0, Math.min(srcBytes.length, 10));
  for (let i = 0; i < 10; i++) {
    payload.push(srcPadded[i]);
  }

  // 13. 固定值 1
  payload.push(1);

  // 14. 校验和版本
  payload.push(CHECKSUM_VERSION);

  // 15. 校验和 XOR
  payload.push(seedByte0 ^ CHECKSUM_XOR_KEY);

  // 16. 校验和固定尾部
  payload.push(...CHECKSUM_FIXED_TAIL);

  return payload;
}

/**
 * CRC32 自定义实现（用于 x-s-common）
 */
export function crc32JsInt(data: Buffer): number {
  // 标准 CRC32 IEEE
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc ^= data[i];
    for (let j = 0; j < 8; j++) {
      crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
    }
  }
  const c = (crc ^ 0xffffffff) >>> 0;

  // 自定义公式: ((0xFFFFFFFF ^ c) ^ 0xEDB88320)
  const poly = 0xedb88320;
  const u = (0xffffffff ^ c ^ poly) >>> 0;

  // 转为有符号 int32
  return u | 0;
}
